import { request } from '@/utils/http'

// ===================== 类型定义 =====================

// 分公司
export interface CreateOfficeRequest {
  parentId: string
  name: string
  nameEn?: string
  code: string
  baseCurrency?: string
  countryId?: string | number
}

export interface UpdateOfficeRequest {
  id: string
  name: string
  nameEn?: string
  code: string
  baseCurrency?: string
  countryId?: string | number | null
}

export interface DeleteOfficeRequest {
  id: string
}

export interface OfficeVO {
  id: string
  name: string
  nameEn?: string
  code?: string
  baseCurrency?: string
  countryId?: string
  description?: string
  parentId?: string
  dateCreated?: string
  dateModified?: string
}

// 部门
export interface CreateDepartmentRequest {
  name: string
  nameEn?: string
  code?: string
  officeId: string
  parentId: string
}

export interface UpdateDepartmentRequest {
  id: string
  name: string
  nameEn?: string
  code?: string | null
}

export interface DeleteDepartmentRequest {
  id: string
}

export interface DepartmentVO {
  id: string
  name: string
  nameEn?: string
  code?: string
  officeId?: string
  parentId?: string
  dateCreated?: string
  dateModified?: string
}

// 角色
export interface CreateRoleRequest {
  departmentId: string
  name: string
  nameEn: string
}

export interface UpdateRoleRequest {
  id: string
  name: string
  nameEn: string
}

export interface DeleteRoleRequest {
  id: string
}

export interface RoleVO {
  id: string
  name: string
  nameEn?: string
  officeId?: string
  departmentId?: string
}

// 权限配置
export interface DataPermissionVO {
  id?: string
  roleId?: string
  privilegeId: string
  scopeType: string            // ALL / BRANCH / DEPARTMENT / PERSONAL
  scopeIds?: string[]
}

export interface RolePrivilegeResultVO {
  privilegeIds: string[]
  dataPermissions: DataPermissionVO[]
}

export interface ConfigRolePrivilegeRequest {
  roleId: string
  privilegeIdList: string[]
  dataPermissions?: DataPermissionVO[]
}

export interface GetRolePrivilegeRequest {
  roleId: string
}

// 平台权限VO
export interface PlatformPrivilegeVO {
  id: string
  parentId?: string
  name: string
  nameEn?: string
  category?: string
  shape?: string
  point?: string
  enableDataScope?: boolean
  description?: string
  dateCreated?: string
  dateModified?: string
}

// 树结构
export interface OfficeTreeVO {
  id: string
  name: string
  nameEn?: string
  code?: string
  parentId?: string
  countryId?: string
  dateCreated?: string
  dateModified?: string
  childOffices?: OfficeTreeVO[]
  childDepartments?: DepartmentTreeVO[]
}

export interface DepartmentTreeVO {
  id: string
  name: string
  code?: string
  parentId?: string
  nameEn?: string
  officeId?: string
  status?: number
  sort?: number
  createTime?: string
  updateTime?: string
  dateCreated?: string
  dateModified?: string
  childDepartments?: DepartmentTreeVO[]
  childRoles?: RoleTreeVO[]
}

export interface RoleTreeVO {
  id: string
  name: string
  code?: string
  parentId?: string
  nameEn?: string
  officeId?: string
  departmentId?: string
  status?: number
  sort?: number
  createTime?: string
  updateTime?: string
  privileges?: PrivilegeVO[]
}

export interface PrivilegeVO {
  id: string
  name: string
  code?: string
  type?: string
  description?: string
  status?: number
  sort?: number
  createTime?: string
  updateTime?: string
}

// ===================== 接口实现 =====================

// 分公司
export function createOffice(data: CreateOfficeRequest) {
  return request.post<OfficeVO>('/sys/tenant/privilege/createOffice', data)
}

export function updateOffice(data: UpdateOfficeRequest) {
  return request.post<OfficeVO>('/sys/tenant/privilege/updateOffice', data)
}

export function deleteOffice(data: DeleteOfficeRequest) {
  return request.post('/sys/tenant/privilege/deleteOffice', data)
}

export function listOffices() {
  return request.post<OfficeVO[]>('/sys/tenant/privilege/office/list')
}

export function getOfficeTree() {
  return request.post<OfficeTreeVO[]>('/sys/tenant/privilege/getOfficeTree')
}

// 部门
export function createDepartment(data: CreateDepartmentRequest) {
  return request.post<DepartmentVO>('/sys/tenant/privilege/createDepartment', data)
}

export function updateDepartment(data: UpdateDepartmentRequest) {
  return request.post<DepartmentVO>('/sys/tenant/privilege/updateDepartment', data)
}

export function deleteDepartment(data: DeleteDepartmentRequest) {
  return request.post('/sys/tenant/privilege/deleteDepartment', data)
}

export function listDepartments() {
  return request.post<DepartmentVO[]>('/sys/tenant/privilege/department/list')
}

// 角色
export function createRole(data: CreateRoleRequest) {
  return request.post<RoleVO>('/sys/tenant/privilege/createRole', data)
}

export function updateRole(data: UpdateRoleRequest) {
  return request.post<RoleVO>('/sys/tenant/privilege/updateRole', data)
}

export function deleteRole(data: DeleteRoleRequest) {
  return request.post('/sys/tenant/privilege/deleteRole', data)
}

export function listRoles() {
  return request.post<RoleVO[]>('/sys/tenant/privilege/role/list')
}

// 权限配置
export function configRolePrivilege(data: ConfigRolePrivilegeRequest) {
  return request.post('/sys/tenant/privilege/configRolePrivilege', data)
}

export function getRolePrivilege(data: GetRolePrivilegeRequest) {
  return request.post<RolePrivilegeResultVO>('/sys/tenant/privilege/getRolePrivilege', data)
}

// 权限列表
export function getPrivilegeList(params?: Record<string, any>) {
  return request.post<PlatformPrivilegeVO[]>('/platform/privilege/list', params)
}

/**
 * 获取当前用户的角色权限代码列表
 * @returns Promise<string[]> 权限代码列表
 */
export function getCurrentUserRolePrivileges(): Promise<string[]> {
  return request.post<string[]>('/sys/tenant/privilege/getCurrentUserRolePrivileges')
}
